import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
  ThumbsUp,
  ThumbsDown,
  Mail,
  Eye,
} from 'lucide-react';
import { apiService } from '@/core/api/service';
import { useAppState } from '@/core/app-context';
import type { FeedbackLog, FeedbackReviewStatus } from '@/core/models';
import { EmptyTenantState } from '@/components/EmptyTenantState';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';

export const Route = createFileRoute('/feedback')({
  head: () => ({
    meta: [{ title: 'Feedback · Replya Internal Backoffice' }],
  }),
  component: FeedbackPage,
});

const LIMIT = 25;

function formatDateTime(iso?: string) {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('it-IT');
}

function RatingBadge({ rating }: { rating: FeedbackLog['rating'] }) {
  if (rating === 'positive') {
    return (
      <Badge variant="outline" className="text-xs text-emerald-600 border-emerald-200 bg-emerald-50">
        <ThumbsUp className="mr-1 h-3 w-3" />
        Positivo
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-xs text-rose-600 border-rose-200 bg-rose-50">
      <ThumbsDown className="mr-1 h-3 w-3" />
      Negativo
    </Badge>
  );
}

const REVIEW_STATUS_META: Record<FeedbackReviewStatus, { label: string; className: string }> = {
  pending: { label: 'Da rivedere', className: 'text-amber-600 border-amber-200 bg-amber-50' },
  confirmed_negative: { label: 'Negativo confermato', className: 'text-rose-600 border-rose-200 bg-rose-50' },
  not_negative: { label: 'Non problematico', className: 'text-slate-500 border-slate-200 bg-slate-50' },
  resolved: { label: 'Risolto', className: 'text-emerald-600 border-emerald-200 bg-emerald-50' },
};

function ReviewStatusBadge({ status }: { status?: FeedbackReviewStatus }) {
  const meta = REVIEW_STATUS_META[status ?? 'pending'];
  return (
    <Badge variant="outline" className={`text-xs ${meta.className}`}>
      {meta.label}
    </Badge>
  );
}

function FeedbackDetailDialog({
  log,
  tenantId,
  open,
  onClose,
}: {
  log: FeedbackLog | null;
  tenantId: string;
  open: boolean;
  onClose: () => void;
}) {
  const [sent, setSent] = useState(false);

  const mutation = useMutation({
    mutationFn: () => apiService.sendFeedbackAlert(tenantId, log?.messageId ?? ''),
    onSuccess: (data) => {
      if (data.sent) {
        const recipients = Array.isArray(data.to) ? data.to.join(', ') : data.to;
        toast.success(`Alert inviato a ${recipients}`);
        setSent(true);
      } else if (data.reason === 'alert_not_configured') {
        toast.error('Alert non configurato per questo tenant. Configuralo in Sector Config.');
      } else if (data.reason === 'alert_disabled') {
        toast.error('Alert automatico disattivato, ma puoi comunque configurare le email in Sector Config.');
      } else {
        toast.error('Impossibile inviare l\'alert');
      }
    },
    onError: (error) => {
      toast.error(`Errore invio alert: ${(error as Error).message}`);
    },
  });

  if (!log) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-2xl border bg-surface max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Dettaglio feedback
            <RatingBadge rating={log.rating} />
          </DialogTitle>
          <DialogDescription>
            {formatDateTime(log.createdAt)} · {log.conversationId}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Messaggio del chatbot</Label>
            <div className="rounded-xl border border-border bg-surface-2 p-3 text-sm whitespace-pre-wrap">
              {log.messageText || 'Messaggio non disponibile'}
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Commento utente</Label>
            <div className="rounded-xl border border-border bg-surface-2 p-3 text-sm whitespace-pre-wrap">
              {log.comment || 'Nessun commento lasciato'}
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Revisione ufficio tecnico</Label>
            <div className="rounded-xl border border-border bg-surface-2 p-3 text-sm space-y-1">
              <ReviewStatusBadge status={log.reviewStatus} />
              {log.reviewNote ? <p className="whitespace-pre-wrap">{log.reviewNote}</p> : null}
              {log.reviewedBy || log.reviewedAt ? (
                <p className="text-xs text-muted-foreground">
                  {[log.reviewedBy, log.reviewedAt ? formatDateTime(log.reviewedAt) : null]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              ) : null}
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Utente</Label>
              <p className="text-sm">{log.userEmail || log.firebaseId || 'anonimo'}</p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Message ID</Label>
              <p className="text-sm font-mono text-xs">{log.messageId}</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            {log.rating === 'negative' && (
              <Button
                onClick={() => mutation.mutate()}
                disabled={mutation.isPending || sent}
                className="rounded-xl bg-brand hover:bg-brand-glow text-brand-foreground"
              >
                <Mail className="mr-2 h-4 w-4" />
                {sent ? 'Alert inviato' : mutation.isPending ? 'Invio...' : 'Invia alert email'}
              </Button>
            )}
            <Button variant="outline" className="rounded-xl" asChild>
              <Link
                to="/conversation/$conversationId"
                params={{ conversationId: log.conversationId }}
                onClick={onClose}
              >
                <MessageSquare className="mr-2 h-4 w-4" />
                Apri conversazione
              </Link>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FeedbackPage() {
  const { selectedTenant } = useAppState();
  const [offset, setOffset] = useState(0);
  const [selectedLog, setSelectedLog] = useState<FeedbackLog | null>(null);
  const [filters, setFilters] = useState({
    conversationId: '',
    rating: 'all' as 'all' | 'positive' | 'negative',
    startDate: '',
    endDate: '',
  });

  const tenantId = selectedTenant?.tenantId ?? '';

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['feedbackLogs', tenantId, filters, offset],
    queryFn: () =>
      apiService.getFeedbackLogs(tenantId, {
        limit: LIMIT,
        offset,
        conversationId: filters.conversationId || undefined,
        rating: filters.rating === 'all' ? undefined : filters.rating,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
      }),
    enabled: !!selectedTenant,
  });

  if (!selectedTenant) {
    return <EmptyTenantState />;
  }

  const total = data?.total ?? 0;
  const hasNext = offset + LIMIT < total;
  const hasPrev = offset > 0;

  const applyFilters = () => {
    setOffset(0);
    refetch();
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <PageHeader
        title="Feedback"
        subtitle={`Valutazioni dei messaggi del chatbot per ${selectedTenant.name}`}
      />

      <Card className="rounded-2xl border bg-surface">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="space-y-1">
              <Label className="text-xs">Conversation ID</Label>
              <Input
                value={filters.conversationId}
                onChange={(e) => setFilters((f) => ({ ...f, conversationId: e.target.value }))}
                placeholder="id conversazione"
                className="rounded-xl bg-surface-2 border-border h-9"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Rating</Label>
              <Select
                value={filters.rating}
                onValueChange={(v) => setFilters((f) => ({ ...f, rating: v as typeof filters.rating }))}
              >
                <SelectTrigger className="rounded-xl bg-surface-2 border-border h-9 w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="positive">Positivo</SelectItem>
                  <SelectItem value="negative">Negativo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Dal</Label>
              <Input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters((f) => ({ ...f, startDate: e.target.value }))}
                className="rounded-xl bg-surface-2 border-border h-9"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Al</Label>
              <Input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters((f) => ({ ...f, endDate: e.target.value }))}
                className="rounded-xl bg-surface-2 border-border h-9"
              />
            </div>
            <Button onClick={applyFilters} className="rounded-xl bg-brand hover:bg-brand-glow text-brand-foreground h-9">
              <Search className="mr-2 h-4 w-4" />
              Filtra
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl border bg-surface">
        <CardHeader>
          <CardTitle className="text-base">
            Risultati {data ? `(${offset + 1}-${Math.min(offset + LIMIT, total)} di ${total})` : ''}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Caricamento...</p>
          ) : isError ? (
            <p className="text-sm text-destructive">
              Errore: {(error as Error).message}
            </p>
          ) : (
            <>
              <div className="overflow-x-auto -mx-2 px-2">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Rating</TableHead>
                      <TableHead>Conversation ID</TableHead>
                      <TableHead>Messaggio</TableHead>
                      <TableHead>Commento</TableHead>
                      <TableHead>Revisione</TableHead>
                      <TableHead>Utente</TableHead>
                      <TableHead className="w-24"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data?.items.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-muted-foreground">
                          Nessun feedback trovato.
                        </TableCell>
                      </TableRow>
                    )}
                    {data?.items.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell className="whitespace-nowrap text-xs tabular-nums">
                          {formatDateTime(log.createdAt)}
                        </TableCell>
                        <TableCell>
                          <RatingBadge rating={log.rating} />
                        </TableCell>
                        <TableCell className="font-mono text-xs max-w-[140px] truncate">
                          {log.conversationId}
                        </TableCell>
                        <TableCell className="max-w-[200px] text-xs">
                          <p className="line-clamp-2">{log.messageText || '—'}</p>
                        </TableCell>
                        <TableCell className="max-w-[200px] text-xs">
                          <p className="line-clamp-2">{log.comment || '—'}</p>
                        </TableCell>
                        <TableCell>
                          <ReviewStatusBadge status={log.reviewStatus} />
                        </TableCell>
                        <TableCell className="text-xs">
                          {log.userEmail || log.firebaseId || 'anonimo'}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="rounded-xl h-8"
                            onClick={() => setSelectedLog(log)}
                          >
                            <Eye className="mr-2 h-3 w-3" />
                            Dettagli
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="flex items-center justify-between mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-xl"
                  onClick={() => setOffset((o) => Math.max(0, o - LIMIT))}
                  disabled={!hasPrev}
                >
                  <ChevronLeft className="mr-2 h-4 w-4" />
                  Precedente
                </Button>
                <span className="text-sm text-muted-foreground">
                  Pagina {Math.floor(offset / LIMIT) + 1} di {Math.max(1, Math.ceil(total / LIMIT))}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-xl"
                  onClick={() => setOffset((o) => o + LIMIT)}
                  disabled={!hasNext}
                >
                  Successiva
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <FeedbackDetailDialog
        log={selectedLog}
        tenantId={tenantId}
        open={selectedLog !== null}
        onClose={() => setSelectedLog(null)}
      />
    </div>
  );
}
